import { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useOffline } from "@/contexts/OfflineContext";
import { cacheVoters, readCachedVoters, enqueueVerify, patchCachedVoter } from "@/lib/offlineDb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { CHANGE_STATUS_LABEL, CHANGE_TYPE_LABEL, STATUS_LIST, STATUS_LABEL, KAWIN_LABEL, KELAMIN_LABEL } from "@/lib/constants";
import { Camera, ClipboardCheck, Plus, UserPlus, Pencil, FileText, X, Search, MapPin, ClipboardList } from "lucide-react";
import { AssignedAreasBadges } from "@/components/AssignedAreas";
import { toast } from "sonner";

export default function CoklitPage() {
  const { user } = useAuth();
  const { online, refreshPending } = useOffline();
  const [tab, setTab] = useState("verify"); // verify | mine
  const [voters, setVoters] = useState([]);
  const [totalVoters, setTotalVoters] = useState(0);
  const [fromCache, setFromCache] = useState(false);
  const [q, setQ] = useState("");
  const [noKkFilter, setNoKkFilter] = useState("");
  const [alamatFilter, setAlamatFilter] = useState("");
  const [coklitStatus, setCoklitStatus] = useState("belum"); // default: tampilkan yang belum dicoklit dulu
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 30;
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(null); // { mode: 'BARU'|'UBAH'|'STATUS', voter? }
  const [mine, setMine] = useState([]);
  const [dusuns, setDusuns] = useState([]);
  const [tpsList, setTpsList] = useState([]);

  const loadVoters = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (q) params.q = q;
      if (noKkFilter.trim()) params.no_kk = noKkFilter.trim();
      if (alamatFilter.trim()) params.alamat = alamatFilter.trim();
      if (coklitStatus !== "all") params.coklit_status = coklitStatus;
      const { data } = await api.get("/voters", { params });
      const items = data.items || [];
      setVoters(items);
      setTotalVoters(data.total || 0);
      setFromCache(false);
      cacheVoters(items).catch(() => {});
    } catch (e) {
      // Network/offline fallback: try IndexedDB cache
      try {
        const cached = await readCachedVoters({ q, limit: 200 });
        if (cached.length > 0) {
          setVoters(cached);
          setFromCache(true);
          if (!navigator.onLine) {
            toast.info(`Mode offline — menampilkan ${cached.length} pemilih dari cache.`);
          }
        } else {
          setVoters([]);
          toast.error(navigator.onLine ? formatApiError(e) : "Tidak ada data offline. Sambungkan internet untuk muat data.");
        }
      } catch {
        toast.error(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMine = async () => {
    try {
      const { data } = await api.get("/voter-changes", { params: { mine: true } });
      setMine(data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    api.get("/dusuns").then(({ data }) => setDusuns(data || []));
    api.get("/tps").then(({ data }) => setTpsList(data || []));
    loadMine();
  }, []);

  useEffect(() => {
    const t = setTimeout(loadVoters, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, noKkFilter, alamatFilter, coklitStatus, page]);

  // Reset ke halaman 1 kalau filter berubah
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, noKkFilter, alamatFilter, coklitStatus]);

  return (
    <div className="space-y-6" data-testid="coklit-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Coklit Lapangan</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
          Pencocokan &amp; Penelitian
        </h1>
        <p className="text-slate-600 text-sm mt-2">
          Verifikasi data pemilih di area tugas Anda. Setiap perubahan akan diverifikasi Panitia.
        </p>
        {user?.role === "ppdp" && (
          <div className="mt-3">
            {user.assigned_tps ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-xs font-semibold">
                <ClipboardCheck className="h-3 w-3" /> Area Tugas: {user.assigned_tps}
              </span>
            ) : (
              <AssignedAreasBadges
                areas={
                  user.assigned_areas?.length
                    ? user.assigned_areas
                    : (user.assigned_dusuns || []).map((d) => ({ dusun: d }))
                }
              />
            )}
          </div>
        )}
      </header>

      {/* Tabs removed — PPDP CRUD langsung tanpa persetujuan; lihat "Perubahan Saya" untuk history */}

      {(
        <>
          {fromCache && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="coklit-cached-banner"
            >
              <b>Mode offline</b> — data ditampilkan dari cache lokal. Tindakan &quot;tandai sudah dicek&quot; akan disinkron otomatis saat online. Penambahan/perubahan pemilih perlu koneksi internet.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama atau NIK" className="pl-9 h-11" data-testid="coklit-search-input" />
            </div>
            <Input
              value={noKkFilter}
              onChange={(e) => setNoKkFilter(e.target.value)}
              placeholder="Filter No. KK"
              className="md:col-span-2 h-11 font-mono"
              data-testid="coklit-filter-no-kk"
            />
            <Input
              value={alamatFilter}
              onChange={(e) => setAlamatFilter(e.target.value)}
              placeholder="Filter alamat"
              className="md:col-span-2 h-11"
              data-testid="coklit-filter-alamat"
            />
            <Select value={coklitStatus} onValueChange={setCoklitStatus}>
              <SelectTrigger className="md:col-span-2 h-11" data-testid="coklit-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="belum">Belum dicoklit</SelectItem>
                <SelectItem value="sudah">Sudah dicoklit</SelectItem>
                <SelectItem value="all">Semua</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => setOpenForm({ mode: "BARU" })}
              disabled={!online}
              title={!online ? "Perlu online untuk menambah pemilih baru" : ""}
              className="bg-red-600 hover:bg-red-700 h-11 md:col-span-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-new-voter-button"
            >
              <UserPlus className="h-4 w-4 mr-2" /> Pemilih Baru
            </Button>
          </div>

          {/* Ringkasan hasil filter */}
          <div className="flex items-center justify-between text-xs text-slate-600" data-testid="coklit-summary">
            <div>
              Menampilkan <b>{voters.length}</b> dari <b>{totalVoters.toLocaleString("id-ID")}</b> pemilih
              {coklitStatus === "belum" && <span className="ml-1 text-amber-800">(belum dicoklit)</span>}
              {coklitStatus === "sudah" && <span className="ml-1 text-emerald-800">(sudah dicoklit)</span>}
            </div>
            {totalVoters > PAGE_SIZE && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                  data-testid="coklit-page-prev"
                >
                  ← Sebelumnya
                </Button>
                <span className="font-mono">Hal {page + 1} / {Math.max(1, Math.ceil(totalVoters / PAGE_SIZE))}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= totalVoters || loading}
                  data-testid="coklit-page-next"
                >
                  Berikutnya →
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {loading && <div className="text-sm text-slate-500">Memuat...</div>}
            {!loading && voters.length === 0 && (
              <div className="col-span-full text-sm text-slate-500 p-6 text-center border border-dashed border-slate-300 rounded-md">
                Tidak ada pemilih di area Anda. Tambahkan pemilih baru bila perlu.
              </div>
            )}
            {voters.map((v) => (
              <VoterCoklitCard
                key={v.id}
                voter={v}
                onEdit={() => setOpenForm({ mode: "UBAH", voter: v })}
                onToggleVerify={async () => {
                  const next = !v.coklit_verified;
                  // Optimistic UI
                  setVoters((arr) => arr.map((x) => x.id === v.id ? { ...x, coklit_verified: next } : x));
                  patchCachedVoter(v.id, { coklit_verified: next }).catch(() => {});
                  try {
                    await api.post(`/voters/${v.id}/verify?verified=${next}`);
                    toast.success(next ? "Ditandai sudah dicek." : "Tanda verifikasi dibuka.");
                  } catch (e) {
                    // If offline (or network error), queue for later sync
                    if (!navigator.onLine || !e?.response) {
                      try {
                        await enqueueVerify(v.id, next);
                        await refreshPending();
                        toast.info("Disimpan offline — akan disinkron saat online kembali.");
                        return;
                      } catch {
                        /* fallthrough to revert */
                      }
                    }
                    // Real server rejection (e.g. 423 locked): revert optimistic update
                    setVoters((arr) => arr.map((x) => x.id === v.id ? { ...x, coklit_verified: !next } : x));
                    patchCachedVoter(v.id, { coklit_verified: !next }).catch(() => {});
                    toast.error(formatApiError(e));
                  }
                }}
              />
            ))}
          </div>

          {/* Pagination bawah — untuk mobile-friendly UX */}
          {totalVoters > PAGE_SIZE && (
            <div className="flex items-center justify-between text-xs text-slate-600 py-3" data-testid="coklit-pagination-bottom">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || loading}>← Sebelumnya</Button>
              <span className="font-mono">Hal {page + 1} / {Math.max(1, Math.ceil(totalVoters / PAGE_SIZE))}</span>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * PAGE_SIZE >= totalVoters || loading}>Berikutnya →</Button>
            </div>
          )}
        </>
      )}

      {openForm && (
        <ChangeFormDialog
          mode={openForm.mode}
          voter={openForm.voter}
          dusuns={dusuns}
          tpsList={tpsList}
          onClose={() => setOpenForm(null)}
          onSubmitted={() => { setOpenForm(null); loadMine(); }}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${
        active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

function VoterCoklitCard({ voter, onEdit, onToggleVerify }) {
  const online = typeof navigator !== "undefined" ? navigator.onLine : true;
  return (
    <article className={`rounded-md border bg-white p-4 flex flex-col ${voter.coklit_verified ? "border-emerald-300 ring-1 ring-emerald-100" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display font-bold text-base truncate">{voter.nama}</div>
          <div className="text-xs font-mono text-slate-500 mt-0.5">{voter.nik}</div>
        </div>
        <StatusBadge status={voter.status} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-700">
        <div><span className="text-slate-500">JK:</span> {KELAMIN_LABEL[voter.jenis_kelamin]}</div>
        <div className="font-mono"><span className="text-slate-500 font-sans">RT/RW:</span> {voter.rt}/{voter.rw}</div>
        <div className="col-span-2 truncate">{voter.alamat}</div>
        <div className="col-span-2 text-slate-500">{voter.dusun} · TPS {voter.tps || "?"}</div>
      </div>
      {voter.coklit_verified && (
        <div className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold">
          <ClipboardCheck className="h-3 w-3" /> Sudah dicek
        </div>
      )}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button
          size="sm"
          variant={voter.coklit_verified ? "outline" : "default"}
          onClick={onToggleVerify}
          className={`font-semibold ${voter.coklit_verified ? "" : "bg-emerald-700 hover:bg-emerald-800 text-white"}`}
          data-testid={`coklit-verify-${voter.id}`}
        >
          <ClipboardCheck className="h-4 w-4 mr-1" /> {voter.coklit_verified ? "Batal" : "Sudah Cek"}
        </Button>
        <Button size="sm" variant="outline" onClick={onEdit} disabled={!online} title={!online ? "Perlu online" : ""} className="font-semibold disabled:opacity-50" data-testid={`coklit-edit-${voter.id}`}>
          <Pencil className="h-4 w-4 mr-1" /> Ubah Data
        </Button>
      </div>
    </article>
  );
}

function MyChanges({ items, reload }) {
  if (items.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 p-8 text-center text-slate-500">
        Belum ada pengajuan. Pengajuan dari Coklit akan muncul di sini.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {items.map((c) => (
        <div key={c.id} className="rounded-md border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display font-bold">
                  {c.data_baru?.nama || c.voter_snapshot?.nama || "—"}
                </span>
                <span className="text-xs font-semibold bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                  {CHANGE_TYPE_LABEL[c.tipe] || c.tipe}
                </span>
              </div>
              {c.alasan && <div className="text-sm text-slate-600 mt-1">Alasan: {c.alasan}</div>}
              <div className="text-xs text-slate-500 mt-1">Diajukan: {new Date(c.submitted_at).toLocaleString("id-ID")}</div>
              {c.catatan && <div className="text-xs text-slate-700 mt-1 italic">Catatan reviewer: {c.catatan}</div>}
            </div>
            <ChangeStatusBadge status={c.status} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ChangeStatusBadge({ status }) {
  const cls = {
    PENDING: "bg-amber-100 text-amber-800 border-amber-200",
    APPROVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
    REJECTED: "bg-red-100 text-red-800 border-red-200",
  }[status] || "bg-slate-100 text-slate-700";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {CHANGE_STATUS_LABEL[status] || status}
    </span>
  );
}

function ChangeFormDialog({ mode, voter, dusuns, tpsList, onClose, onSubmitted }) {
  const { user } = useAuth();
  const [form, setForm] = useState(voter ? { ...voter } : {
    nik: "", nama: "", jenis_kelamin: "L", status: "ACTIVE", disabilitas: 0,
  });
  const [alasan, setAlasan] = useState("");
  const [files, setFiles] = useState([]); // uploaded file records
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pindahCandidate, setPindahCandidate] = useState(null); // voter yg terdeteksi di TPS lain
  const [pindahOpen, setPindahOpen] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const title = mode === "BARU" ? "Tambah Pemilih Baru"
    : mode === "STATUS" ? "Ubah Status Pemilih"
    : "Ubah Data Pemilih";

  // ---- NIK lookup untuk deteksi pindah TPS (hanya untuk mode BARU) ----
  const checkNikForPindah = async (nik) => {
    if (mode !== "BARU" || !nik || nik.length !== 16) return;
    // PPDP hanya bisa memindah ke TPS-nya sendiri
    if (user?.role === "ppdp" && !user?.assigned_tps) return;
    // Dedupe: jangan cek NIK yang sama berulang
    if (pindahCandidate?.voter?.nik === nik) return;
    try {
      const { data } = await api.get(`/pindah-tps/lookup-nik`, { params: { nik } });
      if (data?.exists && data.voter) {
        const my_tps = user?.assigned_tps || null;
        // Kalau voter sudah di TPS PPDP ini → tetap error NIK sudah ada (nanti muncul saat submit)
        if (my_tps && data.voter.tps === my_tps) return;
        setPindahCandidate({ voter: data.voter, my_tps });
        setPindahOpen(true);
      }
    } catch (_e) { /* diam saja, biar submit yang handle error */ }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((f) => [...f, data]);
      toast.success("Bukti terunggah");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "BARU") {
      if (!form.nik || form.nik.length !== 16) { toast.error("NIK 16 digit wajib"); return; }
      if (!form.nama || !form.jenis_kelamin) { toast.error("Nama & JK wajib"); return; }
    }
    setSubmitting(true);
    try {
      let data_baru = {};
      let tipe = mode;
      if (mode === "BARU") {
        data_baru = form;
      } else if (mode === "UBAH") {
        // Send diff vs original
        const orig = voter || {};
        Object.keys(form).forEach((k) => {
          if (form[k] !== orig[k] && form[k] !== undefined) data_baru[k] = form[k];
        });
        if (Object.keys(data_baru).length === 0) { toast.error("Tidak ada perubahan"); setSubmitting(false); return; }
      } else if (mode === "STATUS") {
        data_baru = { status: form.status, keterangan: form.keterangan };
      }
      const res = await api.post("/voter-changes", {
        tipe,
        voter_id: mode === "BARU" ? null : voter?.id,
        data_baru,
        alasan: alasan || null,
        bukti_file_ids: files.map((f) => f.id),
      });
      if (res.data?.status === "PENDING") {
        toast.success("Pengajuan dikirim ke Panitia untuk diverifikasi. Belum masuk DPS sampai disetujui.");
      } else if (res.data?.status === "APPLIED") {
        toast.success("Perubahan langsung diterapkan. Panitia dapat me-revert bila perlu.");
      } else {
        toast.success("Pengajuan terkirim.");
      }
      onSubmitted();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <>
    <Dialog open={!pindahOpen} onOpenChange={(v) => { if (!v && !pindahOpen) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="coklit-form">
          {mode === "STATUS" ? (
            <>
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
                <div className="font-display font-bold">{voter.nama}</div>
                <div className="text-xs text-slate-600 font-mono">{voter.nik}</div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Status baru *</Label>
                <Select value={form.status} onValueChange={(v) => update("status", v)}>
                  <SelectTrigger className="h-11" data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_LIST.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Keterangan</Label>
                <Input value={form.keterangan || ""} onChange={(e) => update("keterangan", e.target.value)} className="h-11" />
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Status Pemilih *" className="md:col-span-2">
                <Select value={form.status || "ACTIVE"} onValueChange={(v) => update("status", v)}>
                  <SelectTrigger className="h-11" data-testid="coklit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_LIST.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="NIK *"><Input maxLength={16} value={form.nik || ""} onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); update("nik", v); if (v.length === 16) checkNikForPindah(v); }} onBlur={(e) => checkNikForPindah(e.target.value.replace(/\D/g, ""))} disabled={mode === "UBAH"} inputMode="numeric" className="font-mono h-11" data-testid="coklit-nik" /></Field>
              <Field label="No. KK"><Input maxLength={16} value={form.no_kk || ""} onChange={(e) => update("no_kk", e.target.value.replace(/\D/g, ""))} className="font-mono h-11" /></Field>
              <Field label="Nama *" className="md:col-span-2"><Input value={form.nama || ""} onChange={(e) => update("nama", e.target.value.toUpperCase())} className="h-11" data-testid="coklit-nama" /></Field>
              <Field label="Tempat Lahir"><Input value={form.tempat_lahir || ""} onChange={(e) => update("tempat_lahir", e.target.value)} className="h-11" /></Field>
              <Field label="Tanggal Lahir"><Input type="date" value={form.tanggal_lahir || ""} onChange={(e) => update("tanggal_lahir", e.target.value)} className="h-11" /></Field>
              <Field label="Jenis Kelamin *">
                <Select value={form.jenis_kelamin} onValueChange={(v) => update("jenis_kelamin", v)}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">Laki-laki</SelectItem><SelectItem value="P">Perempuan</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status Kawin">
                <Select value={form.status_perkawinan || ""} onValueChange={(v) => update("status_perkawinan", v)}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B">Belum Kawin</SelectItem>
                    <SelectItem value="S">Kawin</SelectItem>
                    <SelectItem value="P">Pernah Kawin</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Alamat" className="md:col-span-2"><Input value={form.alamat || ""} onChange={(e) => update("alamat", e.target.value)} className="h-11" /></Field>
              <Field label="RT"><Input value={form.rt || ""} onChange={(e) => update("rt", e.target.value)} className="font-mono h-11" /></Field>
              <Field label="RW"><Input value={form.rw || ""} onChange={(e) => update("rw", e.target.value)} className="font-mono h-11" /></Field>
              <Field label="Dusun">
                <Select value={form.dusun || ""} onValueChange={(v) => update("dusun", v)}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Pilih dusun" /></SelectTrigger>
                  <SelectContent>{dusuns.map((d) => <SelectItem key={d.id} value={d.nama}>{d.nama}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="TPS">
                <Select value={form.tps || ""} onValueChange={(v) => update("tps", v)}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Pilih TPS" /></SelectTrigger>
                  <SelectContent>{tpsList.map((t) => <SelectItem key={t.id} value={t.kode}>{t.kode}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Alasan / Catatan</Label>
            <Input value={alasan} onChange={(e) => setAlasan(e.target.value)} placeholder="cth: ditemukan saat coklit door-to-door" className="h-11" />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold">Bukti pendukung (KTP, KK, foto)</Label>
            <label className="flex items-center justify-center gap-2 h-12 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 text-slate-600 font-semibold cursor-pointer hover:bg-slate-100">
              <Camera className="h-5 w-5" />
              {uploading ? "Mengunggah..." : "Foto / Pilih file"}
              <input
                type="file"
                className="hidden"
                accept="image/*,application/pdf"
                capture="environment"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
              />
            </label>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((f) => (
                  <span key={f.id} className="inline-flex items-center gap-1 text-xs bg-slate-100 border border-slate-200 px-2 py-1 rounded-full">
                    <FileText className="h-3 w-3" /> {f.original_filename}
                    <button type="button" onClick={() => setFiles((arr) => arr.filter((x) => x.id !== f.id))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800" data-testid="coklit-submit">
              {submitting ? "Mengirim..." : "Kirim untuk Verifikasi"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    {pindahOpen && pindahCandidate && (
      <PindahTpsDialog
        candidate={pindahCandidate}
        dusuns={dusuns}
        onClose={() => { setPindahOpen(false); setPindahCandidate(null); }}
        onDone={() => {
          setPindahOpen(false);
          setPindahCandidate(null);
          toast.success("Pemilih berhasil dipindahkan ke TPS Anda.");
          onSubmitted();
        }}
      />
    )}
    </>
  );
}

function PindahTpsDialog({ candidate, dusuns, onClose, onDone }) {
  const { voter, my_tps } = candidate;
  const [phase, setPhase] = useState("confirm"); // confirm | form
  const [alamatBaru, setAlamatBaru] = useState(voter.alamat || "");
  const [dusunBaru, setDusunBaru] = useState(voter.dusun || "");
  const [rwBaru, setRwBaru] = useState(voter.rw || "");
  const [rtBaru, setRtBaru] = useState(voter.rt || "");
  const [alasan, setAlasan] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const doPindah = async () => {
    if (!my_tps) { toast.error("Anda tidak di-assign ke TPS mana pun."); return; }
    if (!alamatBaru.trim() || !dusunBaru.trim() || !rwBaru.trim() || !rtBaru.trim()) {
      toast.error("Alamat, Dusun, RW, dan RT wajib diisi.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/pindah-tps", {
        voter_id: voter.id,
        new_tps: my_tps,
        new_dusun: dusunBaru.trim(),
        new_rw: rwBaru.trim(),
        new_rt: rtBaru.trim(),
        new_alamat: alamatBaru.trim(),
        alasan: alasan || null,
      });
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="pindah-tps-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {phase === "confirm" ? "NIK Sudah Terdaftar di TPS Lain" : "Isi Alamat Baru"}
          </DialogTitle>
        </DialogHeader>
        {phase === "confirm" ? (
          <div className="space-y-4">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm space-y-1">
              <div className="font-display font-bold text-slate-900">{voter.nama}</div>
              <div className="text-xs font-mono text-slate-700">NIK: {voter.nik}</div>
              <div className="text-xs text-slate-700">JK: {voter.jenis_kelamin === "L" ? "Laki-laki" : "Perempuan"}</div>
              <div className="text-xs text-slate-700">
                Alamat saat ini: <span className="font-medium">{voter.alamat || "-"}</span>
                {voter.rt && <>, RT {voter.rt}</>}{voter.rw && <>/RW {voter.rw}</>}
                {voter.dusun && <>, {voter.dusun}</>}
              </div>
              <div className="text-xs text-slate-700 font-semibold">
                Terdaftar di: <span className="text-slate-900">{voter.tps}</span>
              </div>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              Pemilih ini sudah terdaftar di <b>{voter.tps}</b>. Apakah Anda ingin memindahkan pemilih ini ke
              TPS Anda (<b>{my_tps || "-"}</b>)? Data akan langsung dipindahkan dan log akan dicatat untuk direview Panitia.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} data-testid="pindah-batal">Batal</Button>
              <Button
                className="bg-slate-900 hover:bg-slate-800"
                onClick={() => setPhase("form")}
                disabled={!my_tps}
                data-testid="pindah-lanjut"
              >
                Ya, Lanjut Isi Alamat
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 text-xs">
              <span className="font-semibold">{voter.nama}</span> · NIK {voter.nik}
              <div className="text-slate-600">Dari <b>{voter.tps}</b> ke <b>{my_tps}</b></div>
            </div>
            <Field label="Alamat Baru *">
              <Input value={alamatBaru} onChange={(e) => setAlamatBaru(e.target.value)} className="h-11" data-testid="pindah-alamat" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="RT *"><Input value={rtBaru} onChange={(e) => setRtBaru(e.target.value)} className="h-11 font-mono" data-testid="pindah-rt" /></Field>
              <Field label="RW *"><Input value={rwBaru} onChange={(e) => setRwBaru(e.target.value)} className="h-11 font-mono" data-testid="pindah-rw" /></Field>
            </div>
            <Field label="Dusun *">
              <Select value={dusunBaru} onValueChange={setDusunBaru}>
                <SelectTrigger className="h-11" data-testid="pindah-dusun"><SelectValue placeholder="Pilih dusun" /></SelectTrigger>
                <SelectContent>{dusuns.map((d) => <SelectItem key={d.id} value={d.nama}>{d.nama}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Alasan / Catatan">
              <Input value={alasan} onChange={(e) => setAlasan(e.target.value)} placeholder="cth: pindah rumah ke wilayah TPS ini" className="h-11" data-testid="pindah-alasan" />
            </Field>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setPhase("confirm")} disabled={submitting}>Kembali</Button>
              <Button className="bg-slate-900 hover:bg-slate-800" onClick={doPindah} disabled={submitting} data-testid="pindah-submit">
                {submitting ? "Memindahkan..." : "Pindahkan Sekarang"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
    </div>
  );
}
